import type { Money } from "@estelle/domain";

/**
 * Money is formatted, never computed — with one deliberate, narrow exception
 * below for the HSN summary, which cannot exist without summing figures
 * across invoice lines.
 *
 * This mirrors the shared table's private `formatMoney` in
 * `components/table/cells.tsx` rather than importing it: that function is
 * not exported, on purpose, because a table cell and a printed tax document
 * are different enough contexts (this file also has to sum, which a cell
 * renderer never does) that sharing one function would invite the summing
 * logic below to leak into the table.
 */
export function formatMoney(value: Money): string {
  const amount = Number(value);
  if (Number.isNaN(amount)) return value;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * A calendar date, printed the way a tax document prints one — no time, no
 * zone conversion, because `IsoDate` already is a specific day and shifting
 * it by the viewer's zone would print a different date than the one the
 * invoice was issued on.
 */
export function formatDate(value: string): string {
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(parsed);
}

/**
 * `Money` as an integer number of paise.
 *
 * `numeric(12,2)` never carries more than 12 integer digits plus 2 decimals,
 * so `Number(value) * 100` lands on an integer of at most 14 digits — well
 * inside the 53 bits (~16 decimal digits) a JS number represents exactly.
 * That is what makes summing paise as plain integers exact for figures this
 * size, and it is a fact about this specific column width, not a general
 * license to do arithmetic on `Money` — everywhere else in this module a
 * total is read off the row it was stored on, never recomputed.
 */
function toPaise(value: Money): number {
  return Math.round(Number(value) * 100);
}

function fromPaise(paise: number): Money {
  return (paise / 100).toFixed(2);
}

/**
 * Sum a column of `Money` values exactly, for the HSN summary only.
 *
 * Adding the decimal strings as floats (`0.1 + 0.2 !== 0.3`) would drift by
 * a paisa or two over enough rows, and a summary that has to reconcile
 * against a filed return cannot carry that drift. Converting to integer
 * paise first and summing those makes the total exact for the reason
 * `toPaise` explains — an ordinary trick for fixed-point decimals, not a
 * substitute for doing this in `numeric` server-side once there is a server.
 */
export function sumMoney(values: Money[]): Money {
  return fromPaise(values.reduce((total, value) => total + toPaise(value), 0));
}
